import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, LogOut, Lock, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aetheris_admin_token";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const details = (r) =>
  [
    r.birth_date ? `Né(e) le ${r.birth_date}` : null,
    r.sex === "female" ? "Femme" : r.sex === "male" ? "Homme" : null,
    r.maiden_name ? `Nom de jeune fille : ${r.maiden_name}` : null,
  ]
    .filter(Boolean)
    .join(" — ") || "—";

const StatusButton = ({ row, onToggle, testId }) => {
  const isNew = row.status === "nouveau";
  return (
    <button
      data-testid={testId}
      onClick={() => onToggle(row)}
      className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
        isNew
          ? "bg-brand-gold text-brand-ink hover:bg-brand-gold-hover"
          : "border border-white/25 text-white/60 hover:border-brand-gold hover:text-brand-gold"
      }`}
    >
      {isNew ? <Circle size={12} /> : <CheckCircle2 size={12} />}
      {isNew ? "Nouveau" : "Traité"}
    </button>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Administration — Académie AETHERIS";
  }, []);

  const load = useCallback(
    async (tok) => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/admin/bilans`, { headers: { Authorization: `Bearer ${tok}` } });
        if (res.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
          return;
        }
        setRows(await res.json());
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Erreur de connexion");
        return;
      }
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch {
      setError("Erreur de connexion. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (row) => {
    const next = row.status === "nouveau" ? "traite" : "nouveau";
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
    try {
      const res = await fetch(`${API}/admin/bilans/${row.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, status: row.status } : r)));
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRows(null);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-ink px-6">
        <div data-testid="admin-login-card" className="w-full max-w-md border border-white/10 bg-brand-surface p-10">
          <div className="flex items-center gap-2">
            <span className="text-brand-gold text-xs">◆</span>
            <span className="font-serif text-lg tracking-[0.25em] text-white">AETHERIS</span>
          </div>
          <h1 className="mt-8 font-serif text-3xl font-light text-white">Espace Administration</h1>
          <p className="mt-2 text-sm font-light text-white/50">Accès réservé au fondateur.</p>
          <form onSubmit={login} className="mt-8">
            <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/50">Mot de passe</label>
            <div className="relative">
              <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-white/15 bg-brand-ink py-3 pl-11 pr-4 text-sm font-light text-white outline-none transition-colors focus:border-brand-gold"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400">{error}</p>
            )}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="mt-6 w-full bg-brand-gold px-8 py-4 text-sm font-semibold uppercase tracking-wide text-brand-ink transition-all duration-300 hover:bg-brand-gold-hover disabled:opacity-60"
            >
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>
          <Link to="/fr/" className="mt-6 block text-center text-xs font-light text-white/40 hover:text-brand-gold transition-colors">
            ← Retour au site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-ink">
      <header className="border-b border-white/10 bg-brand-surface/60">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-10 py-5">
          <div className="flex items-center gap-2">
            <span className="text-brand-gold text-xs">◆</span>
            <span className="font-serif text-lg tracking-[0.25em] text-white">AETHERIS</span>
            <span className="ml-3 uppercase tracking-luxe text-[10px] text-brand-gold">Administration</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(token)}
              className="border border-white/15 p-2.5 text-white/60 transition-colors hover:border-brand-gold hover:text-brand-gold"
              aria-label="Rafraîchir"
            >
              <RefreshCw size={15} />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs uppercase tracking-wide text-white/60 transition-colors hover:border-brand-gold hover:text-brand-gold"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-12">
        <h1 data-testid="admin-title" className="font-serif text-3xl sm:text-4xl font-light text-white">
          Demandes de Bilan Souveraineté
        </h1>
        <p className="mt-2 text-sm font-light text-white/50">
          {rows ? `${rows.length} demande${rows.length > 1 ? "s" : ""} au total` : "Chargement…"}
        </p>

        {rows && rows.length === 0 && (
          <p data-testid="admin-empty" className="mt-16 text-center text-sm font-light text-white/40">
            Aucune demande pour le moment.
          </p>
        )}

        {rows && rows.length > 0 && (
          <>
            <div className="mt-10 hidden overflow-x-auto border border-white/10 md:block">
              <table data-testid="admin-table" className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-brand-surface">
                    {["Date", "Langue", "Nom", "Email", "Message", "Statut"].map((h) => (
                      <th key={h} className="px-5 py-4 text-[11px] font-medium uppercase tracking-[0.2em] text-brand-gold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`admin-row-${r.id}`}
                      className="border-b border-white/5 transition-colors hover:bg-brand-surface/50"
                    >
                      <td className="whitespace-nowrap px-5 py-4 font-light text-white/60">{formatDate(r.created_at)}</td>
                      <td className="px-5 py-4 uppercase font-light text-white/60">{(r.lang || "fr").toUpperCase()}</td>
                      <td className="px-5 py-4 font-light text-white">{r.first_names} {r.last_name}</td>
                      <td className="px-5 py-4 font-light text-white/60">
                        <a href={`mailto:${r.email}`} className="hover:text-brand-gold transition-colors">{r.email}</a>
                      </td>
                      <td className="max-w-xs px-5 py-4 font-light text-white/50">{details(r)}</td>
                      <td className="px-5 py-4">
                        <StatusButton row={r} onToggle={toggle} testId={`admin-status-${r.id}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-8 space-y-4 md:hidden">
              {rows.map((r) => (
                <div key={r.id} data-testid={`admin-card-${r.id}`} className="border border-white/10 bg-brand-surface p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-light text-white">{r.first_names} {r.last_name}</p>
                      <p className="mt-1 text-xs font-light text-white/40">
                        {formatDate(r.created_at)} · {(r.lang || "fr").toUpperCase()}
                      </p>
                    </div>
                    <StatusButton row={r} onToggle={toggle} testId={`admin-status-mobile-${r.id}`} />
                  </div>
                  <a href={`mailto:${r.email}`} className="mt-3 block text-sm font-light text-brand-gold/90">{r.email}</a>
                  <p className="mt-2 text-xs font-light text-white/50">{details(r)}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
